import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useParent } from '../context/ParentContext';
import { formatXof } from '../utils/format';

export default function DashboardParent() {
  const { user } = useAuth();
  const { children, loading } = useParent();
  const navigate = useNavigate();
  const [childrenData, setChildrenData] = useState([]);
  const [annonces, setAnnonces]         = useState([]);

  useEffect(() => {
    // Charger les données détaillées de chaque enfant
    async function loadChildren() {
      const data = await Promise.all(
        children.map(async (child) => {
          const id = child.id || child.studentId;
          const [evals, absences, factures] = await Promise.all([
            api.get(`/evaluations?classId=${child.enrollments?.[0]?.classId || ''}`).catch(() => []),
            api.get('/registry/presences-eleves').catch(() => []),
            api.get('/finance/unpaid').catch(() => []),
          ]);

          // Calculer la moyenne générale
          const results = (evals || []).flatMap((ev) =>
            (ev.results || []).filter((r) => r.studentId === id && r.totalObtained != null)
              .map((r) => ({ note: r.totalObtained / ev.baremeTotal * 20, coeff: ev.coefficient || 1 }))
          );
          const totalCoeff = results.reduce((s, r) => s + r.coeff, 0);
          const moyenne = totalCoeff > 0
            ? (results.reduce((s, r) => s + r.note * r.coeff, 0) / totalCoeff).toFixed(1)
            : null;

          // Compter les absences
          const allPresences = (Array.isArray(absences) ? absences : (absences?.records || [])).map((r) => ({ ...r.data }));
          const absCount = allPresences.flatMap((r) => r.entries || []).filter((e) => e.studentId === id && e.status === 'ABSENT').length;

          // Solde impayé
          const unpaid = (factures || []).find((f) => f.studentId === id);
          const solde = unpaid?.balance || 0;

          return { ...child, moyenne, absences: absCount, solde };
        })
      );
      setChildrenData(data);
    }

    if (children.length > 0) loadChildren();

    // Annonces
    api.get('/registry/annonces').then((d) => {
      const all = (Array.isArray(d) ? d : (d?.records || [])).map((r) => ({ id: r.id, ...r.data }));
      setAnnonces(all.filter((a) => a.status === 'Publiée').sort((a, b) => (b.publishedAt || '').localeCompare(a.publishedAt || '')).slice(0, 3));
    }).catch(() => {});
  }, [children]);

  if (loading) return <div className="loading">Chargement de votre espace…</div>;

  const prenom = user?.firstName || 'Parent';

  return (
    <>
      {/* En-tête */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: '1.3rem', fontWeight: 600 }}>
          Bonjour, {prenom} 👋
        </h1>
        <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>
          Voici le suivi de {childrenData.length === 1 ? 'votre enfant' : `vos ${childrenData.length} enfants`}
        </p>
      </div>

      {children.length === 0 ? (
        <div className="card">
          <div className="card-body" style={{ padding: 32, textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>👨‍👩‍👧</div>
            <h3 style={{ marginBottom: 8 }}>Aucun enfant associé</h3>
            <p style={{ color: 'var(--muted)', fontSize: 13 }}>
              Votre compte n'est pas encore lié à un élève. Contactez l'administration.
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Cartes enfants */}
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(childrenData.length, 3)}, 1fr)`, gap: 14, marginBottom: 20 }}>
            {childrenData.map((child) => {
              const id = child.id || child.studentId;
              const className = child.enrollments?.[0]?.class?.name || '—';
              const moy = child.moyenne;
              const moyColor = moy ? (Number(moy) >= 10 ? 'var(--success)' : 'var(--danger)') : 'var(--muted)';

              return (
                <div key={id} className="card" style={{ borderTop: '3px solid var(--primary)', cursor: 'pointer' }}
                  onClick={() => navigate(`/parent/enfant/${id}`)}>
                  <div style={{ padding: '16px 18px' }}>
                    {/* Avatar */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                      <div style={{
                        width: 48, height: 48, borderRadius: '50%', flexShrink: 0,
                        background: 'var(--primary)', color: '#fff',
                        display: 'grid', placeItems: 'center',
                        fontWeight: 700, fontSize: '1.1rem',
                      }}>
                        {child.firstName?.[0]}{child.lastName?.[0]}
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{child.lastName} {child.firstName}</div>
                        <div style={{ fontSize: 12, color: 'var(--muted)' }}>{className}</div>
                        <div className="mono" style={{ fontSize: 11, color: 'var(--muted-light)' }}>{child.matricule}</div>
                      </div>
                    </div>

                    {/* KPIs */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                      <div style={{ textAlign: 'center', padding: '8px 4px', background: 'var(--surface-soft)', borderRadius: 8 }}>
                        <div style={{ fontSize: '1.3rem', fontWeight: 700, fontFamily: 'monospace', color: moyColor }}>
                          {moy ?? '—'}
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>Moyenne</div>
                      </div>
                      <div style={{ textAlign: 'center', padding: '8px 4px', background: child.absences > 0 ? 'var(--danger-soft)' : 'var(--surface-soft)', borderRadius: 8 }}>
                        <div style={{ fontSize: '1.3rem', fontWeight: 700, color: child.absences > 0 ? 'var(--danger)' : 'var(--success)' }}>
                          {child.absences}
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>Absences</div>
                      </div>
                      <div style={{ textAlign: 'center', padding: '8px 4px', background: child.solde > 0 ? 'var(--warn-soft)' : 'var(--success-soft)', borderRadius: 8 }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: 700, color: child.solde > 0 ? 'var(--warn)' : 'var(--success)' }}>
                          {child.solde > 0 ? formatXof(child.solde) : '✓'}
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>Solde</div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
                      <button className="btn-ghost btn-sm" style={{ flex: 1, fontSize: 11 }}
                        onClick={(e) => { e.stopPropagation(); navigate(`/parent/bulletin/${id}`); }}>
                        📋 Bulletin
                      </button>
                      <button className="btn-ghost btn-sm" style={{ flex: 1, fontSize: 11 }}
                        onClick={(e) => { e.stopPropagation(); navigate(`/parent/presences/${id}`); }}>
                        ✓ Présences
                      </button>
                      <button className="btn-ghost btn-sm" style={{ flex: 1, fontSize: 11 }}
                        onClick={(e) => { e.stopPropagation(); navigate(`/parent/paiements/${id}`); }}>
                        💰 Paiements
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Annonces */}
          {annonces.length > 0 && (
            <div className="card">
              <div className="card-head">
                <h2>📢 Dernières annonces de l'école</h2>
                <button className="btn-ghost btn-sm" onClick={() => navigate('/communication/annonces')}>Voir tout →</button>
              </div>
              <div className="card-body">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {annonces.map((a) => (
                    <div key={a.id} style={{ padding: '10px 12px', border: '0.5px solid var(--line)', borderRadius: 8 }}>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
                        <span className="badge gold" style={{ fontSize: 10 }}>{a.type}</span>
                        <span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 'auto' }}>
                          {a.publishedAt ? new Date(a.publishedAt).toLocaleDateString('fr-FR') : '—'}
                        </span>
                      </div>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{a.title}</div>
                      <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{(a.content || '').slice(0, 100)}…</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}
